import { ArrowBack } from '@mui/icons-material';
import DoDisturbOnIcon from '@mui/icons-material/DoDisturbOn';
import DoneAllSharpIcon from '@mui/icons-material/DoneAllSharp';
import EditIcon from '@mui/icons-material/Edit';
import EventNoteIcon from '@mui/icons-material/EventNote';
import InsertLinkOutlinedIcon from '@mui/icons-material/InsertLinkOutlined';
import PersonAddAlt1Icon from '@mui/icons-material/PersonAddAlt1';
import WorkIcon from '@mui/icons-material/Work';
import { Box, Chip, Grid, IconButton, List, ListItem, Tooltip } from '@mui/material';
import { Dispatch, ReactElement, SetStateAction, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ActivityLogDTO,
  DncDetailsDTO,
  ILead,
  LogAction,
  LogContextType,
  assignDncDetails,
  saveActivityLog,
  updateLeadStatus,
} from 'wisestep-api';
import {
  getFormatDate,
  WsColors,
  WsDrawer,
  WsIconMoreVer,
  WsIconText,
  WsTypoColors,
  WsTypography,
  WsTypoVariants,
} from 'wisestep-base';

import openNotesFormDialog from '../../../../features/clientDetails/helper/openNotesFormDialog';
import DataPrivacy from '../../../../features/conversationWidget/components/DataPrivacy';
import { useDialog } from '../../../../hooks/useDialog';
import { LeadAccess, useLeadAuthz } from '../../../../hooks/useLeadAuthz';
import getEncodedQueryParams from '../../../../utils/getEncodedQueryParams';
import { openUrlInNewTab } from '../../../../utils/openUrlInNewTab';
import CandidateTags from '../../../clientDetails/components/CandidateTags';
import PopoverOnClick from '../../../clientListingPage/components/PopoverOnClick';
import {
  LeadUpdateDialog,
  defaultLeadActivityLogDTO,
  limitedPrivacyOptions,
  getLeadStatusByName,
} from '../../helper/leadHelper';

import LeadOwnerUpdateDialog from './LeadOwnerUpdateDialog';
import LeadStatusUpdateDialog from './LeadStatusUpdateDialog';
interface ILeadDetailsHeaderProp {
  lead: ILead;
  setLead: Dispatch<SetStateAction<ILead>>;
  queryData: string;
}

const LeadDetailsHeader = ({ lead, setLead, queryData }: ILeadDetailsHeaderProp) => {
  const navigate = useNavigate();
  const [leadUpdateDialog, setLeadUpdateDialog] = useState<LeadUpdateDialog | null>(null);
  const headerEditButtonRef = useRef<HTMLElement | null>(null);
  const isAuthroizedEditLeadDetails = useLeadAuthz(LeadAccess.EDIT_LEAD_DETAILS);
  const [openDialog, closeDialog] = useDialog();
  const [openDNC, setOpenDNC] = useState<boolean>(false);
  const [selectedLead, setSelectedLead] = useState<ILead[]>([]);

  const onSave = async (updatedLead: ILead) => {
    try {
      if (leadUpdateDialog === LeadUpdateDialog.STATUS) {
        const oldStatus = lead.status;
        const newStatus = updatedLead.status;
        const logPayload: ActivityLogDTO = {
          ...defaultLeadActivityLogDTO,
          contextData: {},
          action: LogAction.Edit,
          field: 'Lead Status',
          description: `updated Lead Status from ${oldStatus} to${newStatus} for ${updatedLead.leadName}`,
          entityId: updatedLead.id,
          entityName: updatedLead.leadName,
          parentEntitiyType: LogContextType.Lead,
          parentEntitiyId: updatedLead.id,
        };
        await saveActivityLog(logPayload);
      } else if (leadUpdateDialog === LeadUpdateDialog.OWNER) {
        const oldOwner = lead.leadOwnerInfo?.name;
        const newOwner = updatedLead.leadOwnerInfo?.name;
        const logPayload: ActivityLogDTO = {
          ...defaultLeadActivityLogDTO,
          contextData: {},
          action: LogAction.Edit,
          field: 'Lead Owner',
          description: `updated Lead Owner from ${oldOwner} to ${newOwner} for ${updatedLead.leadName}`,
          entityId: updatedLead.id,
          entityName: updatedLead.leadName,
          parentEntitiyType: LogContextType.Lead,
          parentEntitiyId: updatedLead.id,
        };
        await saveActivityLog(logPayload);
      }

      closeLeadUpdateDialog();
      headerEditButtonRef.current?.click();
      setLead(updatedLead);
    } catch (error) {
      console.error('Error updating lead:', error);
    }
  };

  const closeLeadUpdateDialog = () => setLeadUpdateDialog(null);
  const handleLeadEditClick = () => navigate(`/leads/${lead.id}/edit`);

  const handleDncClick = () => {
    setOpenDNC(!openDNC);
    setSelectedLead(lead);
  };
  const navigateToJobsListing = () => {
    const encodedQueryParams = getEncodedQueryParams({
      clientNames: [lead.clientContact?.clientName],
      clientList: [{ name: lead.clientContact?.clientName }],
      clientRecruiterEmails: [lead.clientContact?.emailAddress],
      clientRecruiterList: [
        {
          clientName: lead.clientContact?.clientName,
          emailAddress: lead.clientContact?.emailAddress,
        },
      ],
      jobStatus: 'All',
      query: '',
      page: 0,
      size: 20,
      sort: [],
      workspaceId: '',
    });
    openUrlInNewTab(`/jobs?d=${encodedQueryParams}`);
  };
  const isInDncValue = lead.clientContact?.dncDetails?.isInDnc;

  const headerEditMenu: { text: string; icon: ReactElement; onClick: () => void }[] = [
    {
      text: 'Add Note',
      icon: <EventNoteIcon sx={{ height: 22, width: 22, mr: 0.5, color: WsColors.TheBlues500 }} />,
      onClick: () => {
        openNotesFormDialog({
          clientId: lead?.client?.id ?? '',
          contactId: lead.clientContact?.id ?? '',
          clientName: lead.client?.name,
          contactName: lead.clientContact?.firstName,
          isClient: false,
          openDialog: openDialog,
          closeDialog: closeDialog,
        });
      },
    },
    ...(isAuthroizedEditLeadDetails
      ? [
          {
            text: 'Update Status',
            icon: (
              <DoneAllSharpIcon
                sx={{ height: 22, width: 22, mr: 0.5, color: WsColors.TheBlues500 }}
              />
            ),
            onClick: () => setLeadUpdateDialog(LeadUpdateDialog.STATUS),
          },
          {
            text: 'Change Lead Owner',
            icon: (
              <PersonAddAlt1Icon
                sx={{ height: 23, width: 23, mr: 0.5, color: WsColors.TheBlues500 }}
              />
            ),
            onClick: () => setLeadUpdateDialog(LeadUpdateDialog.OWNER),
          },
          {
            text: 'Edit Lead',
            icon: <EditIcon sx={{ height: 22, width: 22, mr: 0.5, color: WsColors.TheBlues500 }} />,
            onClick: handleLeadEditClick,
          },
        ]
      : []),
    {
      text: 'Jobs',
      icon: <WorkIcon sx={{ height: 22, width: 22, mr: 0.5, color: WsColors.TheBlues500 }} />,
      onClick: navigateToJobsListing,
    },
    ...(!isInDncValue
      ? [
          {
            text: 'Mark DNC',
            icon: (
              <DoDisturbOnIcon
                sx={{ height: 22, width: 22, mr: 0.5, color: WsColors.TheBlues500 }}
              />
            ),
            onClick: handleDncClick,
          },
        ]
      : []),
  ];

  const handleDncSubmit = async (leadId: string, contactId: string, data: DncDetailsDTO) => {
    try {
      const leadStatus = await getLeadStatusByName('Closed');
      await assignDncDetails(contactId, data);
      if (data.isInDnc) {
        await updateLeadStatus({
          id: leadId,
          leadName: '',
          source: '',
          leadStatus: { id: leadStatus.id },
        } as ILead);
      }
    } catch (error) {
      console.error('Error updating DNC details / lead status:', error);
    }
  };
  return (
    <Box bgcolor={WsColors.TheBlues900} borderRadius={1.5} p={2} width="100%">
      <Grid container direction="row" alignItems="center">
        <Grid item>
          <IconButton sx={{ color: WsColors.White }}>
            <ArrowBack onClick={() => navigate(`/leads?d=${queryData}`)} />
          </IconButton>
        </Grid>
        <Grid item>
          <WsTypography variant={WsTypoVariants.TITLE} color={WsTypoColors.WHITE} cursor="pointer">
            {lead.leadName || ''}
          </WsTypography>
        </Grid>
        {lead.linkedinUrl && (
          <Grid item>
            <Tooltip title="Job URL">
              <IconButton
                onClick={() => {
                  const url = lead.linkedinUrl?.startsWith('http')
                    ? lead.linkedinUrl
                    : `https://${lead.linkedinUrl}`;
                  openUrlInNewTab(url);
                }}
              >
                <InsertLinkOutlinedIcon color={WsTypoColors.SECONDARY} />
              </IconButton>
            </Tooltip>
          </Grid>
        )}
        <Grid item ml={1}>
          <Chip
            label={lead?.leadStatus?.leadStatusName}
            size="small"
            sx={{
              bgcolor: WsColors.TheBlues500,
              color: WsTypoColors.WHITE,
              borderRadius: 1,
              fontWeight: 'bold',
              fontSize: 14,
            }}
          />
        </Grid>
        <Grid item sx={{ ml: 'auto' }}>
          <PopoverOnClick
            element={
              <Box ref={headerEditButtonRef}>
                <WsIconMoreVer />
              </Box>
            }
          >
            <List
              sx={{
                bgcolor: WsColors.White,
                p: 1,
              }}
            >
              {headerEditMenu.map((menuItem) => (
                <ListItem key={menuItem.text} sx={{ pr: 0, py: 0.7 }}>
                  <WsIconText
                    text={menuItem.text}
                    textCursor="pointer"
                    icon={menuItem.icon}
                    textSize={WsTypoVariants.BODY1}
                    textColor={WsTypoColors.TERTIARY900}
                    onClick={() => menuItem.onClick()}
                  />
                </ListItem>
              ))}
            </List>
            {leadUpdateDialog == LeadUpdateDialog.STATUS && (
              <LeadStatusUpdateDialog
                onSave={onSave}
                onCancel={closeLeadUpdateDialog}
                lead={lead}
              />
            )}
            {leadUpdateDialog == LeadUpdateDialog.OWNER && (
              <LeadOwnerUpdateDialog onSave={onSave} onCancel={closeLeadUpdateDialog} lead={lead} />
            )}
          </PopoverOnClick>
        </Grid>
      </Grid>
      <Grid container>
        <Grid item pl={5}>
          <WsTypography variant={WsTypoVariants.BODY2} color={WsTypoColors.WHITE} cursor="pointer">
            Created on {getFormatDate(lead.createdOn ?? '')}
          </WsTypography>
        </Grid>
        <Grid item pl={2}>
          <WsTypography variant={WsTypoVariants.BODY2} color={WsTypoColors.WHITE} cursor="pointer">
            Last Activity: {getFormatDate(lead.modifiedOn ?? '')}
          </WsTypography>
        </Grid>
      </Grid>
      {lead.tags && (
        <Grid container direction="row" spacing={2} pl={5} mt={0.2}>
          <CandidateTags tags={lead.tags} initialLimit={5} />
        </Grid>
      )}
      {openDNC && selectedLead && (
        <WsDrawer open={openDNC} onClose={() => setOpenDNC(!openDNC)}>
          <DataPrivacy
            clientContactParentId={selectedLead?.clientContact?.clientId}
            clientContactId={selectedLead.clientContact?.id}
            clientContactName={selectedLead.clientContact?.firstName}
            email={selectedLead?.clientContact?.createdBy ?? ''}
            candidateName={selectedLead?.leadName ?? ''}
            isInDnc={selectedLead?.clientContact?.dncDetails?.isDnc ?? false}
            dncDetails={selectedLead?.clientContact?.dncDetails}
            privacyOptions={limitedPrivacyOptions}
            restrictLabel="DNC Up to*"
            entityId={selectedLead?.id}
            parentEntitiyId={selectedLead?.id ?? ''}
            entityName={selectedLead?.leadName}
            logContextType={LogContextType.Lead}
            onSubmitApi={(data) =>
              handleDncSubmit(selectedLead.id, selectedLead.clientContact?.id, data)
            }
            enableActivityLog={true}
          />
        </WsDrawer>
      )}
    </Box>
  );
};

export default LeadDetailsHeader;
